<template>
    <div class="pie-chart-template">
       <div class="pie-chart-container" :class="{ hide: !if_chart_has_data }">
           <div class="pie-chart-wrapper">
               <canvas :id="`chart-${id}`" class="pie-chart"></canvas>
           </div>
           <div :id="`legend-${id}`" class="pie-chart-legend"></div>
       </div>
       <div :class="{ 'd-inline-block': !if_chart_has_data && !if_refreshing, hide: if_chart_has_data || if_refreshing }" class="text-center">
           <h1 class="text-secondary mb-1"><i class="fas fa-chart-simple"></i></h1>
           <h4 class="text-secondary mb-2"><em>Press the <strong>Preview</strong> button to render the chart</em></h4>
       </div>
   </div>
</template>

<script>
import { defineComponent, markRaw } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTooltip from '../../../../mixins/chart_tooltip';
import chartBase from '../../../../mixins/chart_base';

export default defineComponent({
    name: 'PieChartTemplate',
    mixins: [chartTooltip, chartBase],
    props: {
        chart_selected: {
            type: Object,
            required: true
        },
        id: {
            type: [String, Number],
            required: true
        },
        if_refreshing: {
            type: Boolean,
            default: false
        },
        if_on_report_page: {
            type: Boolean,
            default: false
        },
        if_benchmark_report: {
            type: Boolean,
            default: false
        },
        applied_chart_filter: {
            type: Object,
            default: () => ({})
        },
        if_show_label: {
            type: Boolean,
            default: false
        },
        section_id: {
            type: [String, Number],
            default: null
        },
        show_compressed_numbers: {
            type: Boolean,
            default: true
        },
        forceShowAllData: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            format_type: 'number',
            color: 'yellow',
            total_value: 0,
            /** When true, next chart render (e.g. after label toggle) uses no animation. Cleared after render. */
            skipAnimationForNextRender: false,
        };
    },
    watch: {
        applied_chart_filter: {
            handler(newVal) {
                this.if_chart_filter_applied = newVal?.if_chart_filter_applied && this.id === newVal?.section_chart_id;
                // Sync chart_filter_label from applied_chart_filter when filter is applied to this chart
                if (this.if_chart_filter_applied && newVal?.chart_filters && newVal.chart_filters.length > 0) {
                    this.chart_filter_label = newVal.chart_filters.map(filter => filter.value);
                } else if (!this.if_chart_filter_applied) {
                    this.chart_filter_label = [];
                }
                this.debouncedRender();
            },
            deep: true
        },
        if_show_label() {
            this.skipAnimationForNextRender = true;
        },
    },
    methods: {
        renderChart() {
            if (!this.chart_selected?.data) return;

            const dataset = this.getChartData();
            const options = this.getChartOptions(dataset);

            this.destroyChart();

            // Wait for the next tick to ensure DOM is ready and previous chart is fully destroyed
            this.$nextTick(() => {
                const ctx = document.getElementById('chart-' + this.id);
                if (ctx?.getContext) {
                    this.createChartWhenVisible(ctx, options, (chartInstance) => {
                        this.chart = markRaw(chartInstance);
                        this.setupChartMouseLeave(chartInstance);
                        this.skipAnimationForNextRender = false;
                    });
                }
            });
        },
        debouncedRender() {
            if (this.render_timeout) {
                clearTimeout(this.render_timeout);
            }
            this.render_timeout = setTimeout(() => {
                this.$nextTick(() => {
                    this.format_type = this.chart_selected?.metric_formats?.[0] || 'number';
                    this.color = this.chart_selected?.metrics?.[0]?.color || 'yellow';
                    this.num_of_datasets = Object.keys(this.chart_selected.data).length;
                    this.if_chart_has_data = true;
                    this.renderChart();
                });
            }, 200); // 200ms debounce
        },
        getChartData() {
            const chartColors = this.getColor(this.color); // the array of RGB color values indicated by the database
            const datasetArray = [];
            let max = 0;
            let total = 0;

            let backgroundColorArray = [];
            if (this.if_chart_filter_applied){
                const index = this.chart_selected.labels.findIndex(label => (label || '(not set)') == this.chart_filter_label[0]);
                this.chart_filter_label_index[0] = index;
                
                // For Pie charts, we typically only have one dimension, but we use grayscale for non-selected slices
                backgroundColorArray = this.getColor('gray').slice(-this.chart_selected.labels.length).reverse();
                if (index >= 0) {
                    backgroundColorArray[index] = this.getColorByLetter(this.color, 'A'); // Use color A
                }
            }
            else {
                backgroundColorArray = chartColors;
            }

            Object.keys(this.chart_selected.data).forEach((key, index) => {
                const max_temp = Math.max(...this.chart_selected.data[key]);
                if (max_temp > max){
                    max = max_temp;
                }
                total = Object.values(this.chart_selected.data)[0].reduce((partialSum, a) => partialSum + a, 0);
                datasetArray.push({
                    label: key,
                    data: this.chart_selected.data[key],
                    backgroundColor: backgroundColorArray,
                    borderWidth: 0,
                    hoverOffset: 10,
                })
            });

            this.max_value = max;
            this.total_value = total;
            return {
                labels: this.chart_selected.labels,
                datasets: datasetArray
            };
        },
        getChartOptions(data) {
            const self = this;
            
            // Custom HTML Legend Plugin
            const htmlLegendPlugin = {
                id: 'htmlLegend',
                afterUpdate(chart, args, options) {
                    const containerID = options.containerID;
                    const legendContainer = document.getElementById(containerID);
                    
                    if (!legendContainer) return;
                    
                    // Clear previous legend
                    legendContainer.innerHTML = '';
                    
                    // Get legend items
                    const meta = chart.getDatasetMeta(0);
                    const items = chart.data.labels.map((label, i) => {
                        const dataset = chart.data.datasets[0];
                        const value = dataset.data[i];
                        const total = dataset.data.reduce((sum, val) => sum + val, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        const backgroundColor = dataset.backgroundColor[i];
                        const isHidden = meta.data[i]?.hidden || false;
                        
                        return { label, value, percentage, backgroundColor, index: i, isHidden };
                    });
                    
                    // Create legend items
                    items.forEach(item => {
                        const legendItem = document.createElement('div');
                        legendItem.style.cssText = 'display: flex; align-items: flex-start; margin-bottom: 12px; cursor: pointer;';
                        
                        // Color box
                        const colorBox = document.createElement('span');
                        colorBox.style.cssText = `
                            width: 12px;
                            height: 12px;
                            border-radius: 50%;
                            background-color: ${item.backgroundColor};
                            margin-right: 8px;
                            flex-shrink: 0;
                            margin-top: 3px;
                        `;
                        
                        // Text container
                        const textContainer = document.createElement('div');
                        textContainer.style.cssText = 'line-height: 1.4; word-wrap: break-word; max-width: 200px;';
                        
                        // Label (bold)
                        const labelSpan = document.createElement('div');
                        labelSpan.style.cssText = `font-weight: 700; font-family: DM Sans; ${item.isHidden ? 'text-decoration: line-through;' : ''}`;
                        labelSpan.textContent = item.label;
                        
                        // Value and percentage (regular)
                        const valueSpan = document.createElement('div');
                        valueSpan.style.cssText = `font-weight: 400; font-family: DM Sans; ${item.isHidden ? 'text-decoration: line-through;' : ''}`;
                        
                        // Format the value using the same logic as data label formatter
                        let formattedValue = '';
                        if(self.format_type == 'money') {
                            if(self.show_compressed_numbers && item.value >= 1000) {
                                formattedValue = self.currency_abbr(item.value);
                            } else {
                                formattedValue = self.currency_with_zero(item.value);
                            }
                        } else if(self.format_type == 'number') {
                            if(self.show_compressed_numbers && item.value >= 1000) {
                                formattedValue = self.num_abbr(item.value);
                            } else if(self.max_value >= 10) {
                                formattedValue = self.number_with_zero(item.value);
                            } else {
                                formattedValue = self.number_with_decimal(item.value);
                            }
                        } else if(self.format_type == 'percent') {
                            formattedValue = self.percentage(item.value);
                        } else {
                            formattedValue = item.value;
                        }
                        
                        valueSpan.textContent = `${formattedValue} (${item.percentage}%)`;
                        
                        textContainer.appendChild(labelSpan);
                        textContainer.appendChild(valueSpan);
                        
                        legendItem.appendChild(colorBox);
                        legendItem.appendChild(textContainer);
                        
                        // Click handler to toggle data visibility
                        legendItem.onclick = () => {
                            const meta = chart.getDatasetMeta(0);
                            meta.data[item.index].hidden = !meta.data[item.index].hidden;
                            chart.update();
                        };
                        
                        legendContainer.appendChild(legendItem);
                    });
                }
            };
            
            const config = {
                plugins: [ChartDataLabels, htmlLegendPlugin],
                type: 'pie',
                data: data,
                options: {
                    onHover: (event, activeElements, chart) => {
                        if (activeElements && activeElements.length > 0) {
                            // Clear any pending hide timeout when hovering over an element
                            if (this.hideTimeout) {
                                clearTimeout(this.hideTimeout);
                                this.hideTimeout = null;
                            }
                            
                            const activeElement = activeElements[0];
                            const dataIndex = activeElement.index;
                            
                            // If hovering over a different data point OR returning to a point that's not currently hovered,
                            // (re)start the debounce timer
                            if (dataIndex !== this.pendingHoverIndex || this.hoveredIndex !== dataIndex) {
                                // Cancel any pending show timeout
                                if (this.hoverTimeout) {
                                    clearTimeout(this.hoverTimeout);
                                }
                                
                                // Reset current state immediately when moving to a new point
                                // to keep highlight and tooltip in sync (both hidden during the debounce)
                                if (this.hoveredIndex !== null && this.hoveredIndex !== dataIndex) {
                                    this.hoveredIndex = null;
                                    chart.update('none');
                                }
                                
                                // Reset flags for new data point
                                this.pendingHoverIndex = dataIndex;
                                
                                // Start new debounce timer - 200ms delay to show hover
                                this.hoverTimeout = setTimeout(() => {
                                    this.hoverTimeout = null;
                                    // Apply hover effect after delay
                                    if (this.hoveredIndex !== dataIndex) {
                                        this.hoveredIndex = dataIndex;
                                        chart.update('none'); // Immediate update for slice and tooltip
                                    }
                                }, 200);
                            }
                        } else {
                            // Clear any pending show timeout when leaving
                            if (this.hoverTimeout) {
                                clearTimeout(this.hoverTimeout);
                                this.hoverTimeout = null;
                                this.pendingHoverIndex = null;
                            }
                            
                            // Debounce hiding the hover state (200ms delay)
                            if (this.hoveredIndex !== null && !this.hideTimeout) {
                                this.hideTimeout = setTimeout(() => {
                                    // Only hide if still not hovering and tooltip is also not being hovered
                                    if (this.hoveredIndex !== null && !this.isTooltipHovered) {
                                        this.hoveredIndex = null;
                                        chart.update('none'); // Immediate update for slice and tooltip
                                        
                                        // Explicitly hide tooltip element to stay in sync
                                        let tooltipEl = document.getElementById('chart-' + this.id + '-' + this.effectiveSectionId + '-tooltip');
                                        if (tooltipEl) {
                                            tooltipEl.style.opacity = 0;
                                            tooltipEl.style.pointerEvents = 'none';
                                        }
                                    }
                                    this.hideTimeout = null;
                                }, 200);
                            }
                        }
                    },
                    animation: {
                        duration: this.skipAnimationForNextRender ? 0 : 500,
                    },
                    responsive: true,
                    maintainAspectRatio: true,
                    layout: {
                        padding: {
                            top: 15,
                            bottom: 15,
                            left: 10,
                            right: 10
                        }
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        avidExportLegend: {
                            formatValue(v) {
                                const n = typeof v === 'number' && Number.isFinite(v)
                                    ? v
                                    : (v != null && typeof v === 'object' && typeof v.y === 'number' && Number.isFinite(v.y))
                                        ? v.y
                                        : (v != null && typeof v === 'object' && typeof v.raw === 'number' && Number.isFinite(v.raw))
                                            ? v.raw
                                            : 0;
                                if (self.format_type == 'money') {
                                    if (self.show_compressed_numbers && n >= 1000) {
                                        return self.currency_abbr(n);
                                    }
                                    return self.currency_with_zero(n);
                                }
                                if (self.format_type == 'number') {
                                    if (self.show_compressed_numbers && n >= 1000) {
                                        return self.num_abbr(n);
                                    }
                                    if (self.max_value >= 10) {
                                        return self.number_with_zero(n);
                                    }
                                    return self.number_with_decimal(n);
                                }
                                if (self.format_type == 'percent') {
                                    return self.percentage(n);
                                }
                                return n.toLocaleString();
                            },
                        },
                        htmlLegend: {
                            containerID: `legend-${this.id}`,
                        },
                        tooltip: {
                            enabled: false,
                            external(context) {
                                const chartCanvasId = context.chart?.canvas?.id;
                                if (!chartCanvasId || !chartCanvasId.startsWith('chart-')) return;

                                const chartIdFromCanvas = chartCanvasId.replace(/^chart-/, '');
                                const expectedTooltipId = self.getTooltipId();
                                if (chartIdFromCanvas !== String(self.id)) {
                                    let tooltipEl = document.getElementById(expectedTooltipId);
                                    if (tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                    }
                                    return;
                                }

                                let tooltipEl = document.getElementById('chart-'+self.id+'-'+self.effectiveSectionId+'-tooltip');

                                if (!tooltipEl) {
                                    tooltipEl = document.createElement('div');
                                    tooltipEl.id = 'chart-'+self.id+'-'+self.effectiveSectionId+'-tooltip';
                                    tooltipEl.classList.add("chart-template-tooltip");
                                    tooltipEl.innerHTML = '<table></table>';
                                    document.body.appendChild(tooltipEl);

                                    tooltipEl.addEventListener('wheel', (e) => {
                                        e.preventDefault();
                                        const deltaY = e.deltaY;
                                        if (deltaY) {
                                            window.scrollBy({ top: deltaY, behavior: 'auto' });
                                        }
                                    }, { passive: false });

                                    tooltipEl.addEventListener('mouseenter', () => {
                                        if (self.hideTimeout) {
                                            clearTimeout(self.hideTimeout);
                                            self.hideTimeout = null;
                                        }
                                        self.isTooltipHovered = true;
                                        tooltipEl.style.opacity = 1;
                                        tooltipEl.style.pointerEvents = 'auto';
                                    });
                                    tooltipEl.addEventListener('mouseleave', () => {
                                        self.isTooltipHovered = false;
                                        
                                        if (self.hideTimeout) {
                                            clearTimeout(self.hideTimeout);
                                        }
                                        self.hideTimeout = setTimeout(() => {
                                            if (!self.isTooltipHovered && tooltipEl) {
                                                tooltipEl.style.opacity = 0;
                                                tooltipEl.style.pointerEvents = 'none';

                                                if (self.hoveredIndex !== null) {
                                                    self.hoveredIndex = null;
                                                    if (self.chart) {
                                                        self.chart.update('none');
                                                    }
                                                }
                                            }
                                            self.hideTimeout = null;
                                        }, 200);
                                    });
                                }

                                const tooltipIdPrefix = 'chart-' + chartIdFromCanvas + '-';
                                if (!tooltipEl.id.startsWith(tooltipIdPrefix) || !tooltipEl.id.endsWith('-tooltip') || tooltipEl.id !== expectedTooltipId) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    return;
                                }

                                const position = context.chart.canvas.getBoundingClientRect();
                                const tooltipModel = context.tooltip;
                                if (tooltipModel?.body?.[0]?.lines?.[0]) {
                                    tooltipModel.title = tooltipModel.body[0].lines[0].split(': ');
                                }

                                if (!tooltipModel || tooltipModel.opacity === 0) {
                                    if (!self.isTooltipHovered && self.hoveredIndex === null && tooltipEl) {
                                        tooltipEl.style.opacity = 0;
                                        tooltipEl.style.pointerEvents = 'none';
                                        self.hideAllChartTooltips();
                                    }
                                    return;
                                }
                                
                                const currentDataIndex = tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0 
                                    ? tooltipModel.dataPoints[0].dataIndex 
                                    : null;
                                
                                if (currentDataIndex === null || currentDataIndex !== self.hoveredIndex) {
                                    tooltipEl.style.opacity = 0;
                                    tooltipEl.style.pointerEvents = 'none';
                                    self.hideAllChartTooltips();
                                    return;
                                }

                                // Hide tooltips from other sections
                                const allTooltips = document.querySelectorAll('[id^="chart-"][id$="-tooltip"]');
                                allTooltips.forEach(tt => {
                                    if (tt.id !== tooltipEl.id) {
                                        tt.style.opacity = 0;
                                        tt.style.pointerEvents = 'none';
                                    }
                                });

                                // Display, position, and set styles for font with glassmorphism effect
                                tooltipEl.style.opacity = 1;
                                tooltipEl.style.pointerEvents = 'auto';
                                tooltipEl.style.position = 'absolute';
                                tooltipEl.style.left = position.left + window.scrollX + tooltipModel.caretX - tooltipModel.width/2 + 'px';
                                tooltipEl.style.top = position.top + window.scrollY + tooltipModel.caretY + 'px';
                                tooltipEl.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
                                tooltipEl.style.webkitBackdropFilter = 'blur(30px) saturate(180%)';
                                tooltipEl.style.backdropFilter = 'blur(30px) saturate(180%)';
                                tooltipEl.style.padding = '10px 20px';
                                tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                tooltipEl.style.border = '1px solid rgba(255, 255, 255, 0.3)';
                                tooltipEl.style.fontFamily = "'Nunito', sans-serif";
                                tooltipEl.style.transition = 'opacity 300ms ease-out';

                                // Set caret Position
                                tooltipEl.classList.remove('above', 'below', 'no-transform');
                                if (tooltipModel.yAlign) {
                                    tooltipEl.classList.add(tooltipModel.yAlign);
                                } else {
                                    tooltipEl.classList.add('no-transform');
                                }

                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(b => b.lines);

                                    let innerHtml = '<thead>';
                                    innerHtml += '<tr><th style="font-weight: 500; font-size: 18px; padding-bottom: 4px; background-color: transparent;">' + titleLines[0] + '</th></tr>';
                                    innerHtml += '</thead><tbody>';

                                    bodyLines.forEach((body, i) => {
                                        const body_label = body[0].split(': ');
                                        const value = tooltipModel.dataPoints[i].raw;

                                        const colors = tooltipModel.labelColors[i];
                                        const textColor = colors.backgroundColor;

                                        let formattedValue = '';
                                        if(self.format_type == 'money') {
                                            if(self.show_compressed_numbers && value >= 1000) {
                                                formattedValue = self.currency_abbr(value);
                                            } else if(self.max_value >= 10) {
                                                formattedValue = self.currency_with_zero(value);
                                            } else {
                                                formattedValue = self.currency_with_decimal(value);
                                            }
                                        } else if(self.format_type == 'number') {
                                            if(self.show_compressed_numbers && value >= 1000) {
                                                formattedValue = self.num_abbr(value);
                                            } else if(self.max_value >= 10) {
                                                formattedValue = self.number_with_zero(value);
                                            } else {
                                                formattedValue = self.number_with_decimal(value);
                                            }
                                        } else if(self.format_type == 'percent') {
                                            formattedValue = self.percentage(value);
                                        }
                                        
                                        const percentageText = ' ('+ self.percentage(value / self.total_value) +')';

                                        innerHtml += '<tr><td style="padding-top: 1px; padding-bottom: 1px;"><span style="color: ' + textColor + '; font-weight: 400;">' + formattedValue + '</span><span style="color: #111921; font-weight: 400;">' + percentageText + '</span></td></tr>';
                                    });

                                    if (self.if_on_report_page && !self.if_benchmark_report) {
                                        const isFilterApplied = self.if_chart_filter_applied && self.chart_filter_label?.[0] == titleLines[0];

                                        innerHtml += '<div class="d-flex justify-content-end my-2">' +
                                            '<button class="btn btn-sm btn-primary d-flex align-items-center px-4" style="font-weight: 500;">' +
                                            '<img class="icon me-1" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">' + (isFilterApplied ? 'Remove as Filter' : 'Filter') +
                                            '</button>' +
                                            '</div>';

                                        innerHtml += '</tbody>';

                                        const tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;

                                        const btn = tooltipEl.querySelector('button');
                                        if (self.if_chart_filter_applied && self.chart_filter_label?.[0] != titleLines[0]) {
                                            btn.disabled = true;
                                        }

                                        if (self.applied_chart_filter.if_chart_filter_applied && self.if_chart_filter_applied == false) {
                                            btn.disabled = true;
                                        }

                                        if (self.if_chart_filter_applied) {
                                            btn.addEventListener('click', () => {
                                                self.if_chart_filter_applied = false;
                                                self.chart_filter_label = [];
                                                self.$emit('exploreChart', {
                                                    chart_selected: null,
                                                    chart_filters: []
                                                });
                                            });
                                        } else {
                                            btn.addEventListener('click', () => {
                                                self.if_chart_filter_applied = true;
                                                self.chart_filter_label = [titleLines[0]];
                                                self.$emit('exploreChart', {
                                                    chart_selected: self.chart_selected,
                                                    chart_filters: [{
                                                        name: self.chart_selected.group_by[0].text,
                                                        column: self.chart_selected.group_by[0].value,
                                                        value: titleLines[0]
                                                    }]
                                                });
                                            });
                                        }
                                    } else {
                                        innerHtml += '</tbody>';
                                        const tableRoot = tooltipEl.querySelector('table');
                                        tableRoot.innerHTML = innerHtml;
                                    }
                                }
                            }
                        },
                        datalabels: {
                            anchor: 'center',
                            align: 'center',
                            textAlign: 'center',
                            padding: 6,
                            borderRadius: 4,
                            // backgroundColor(context) {
                            //     if (!context.dataset || !context.dataset.backgroundColor || !context.dataset.backgroundColor[context.dataIndex]) {
                            //         return 'rgba(0, 0, 0, 0.7)';
                            //     }
                            //     const color = context.dataset.backgroundColor[context.dataIndex];
                            //     return color.replace('rgb', 'rgba').replace(')', ', 0.7)');
                            // },
                            labels: {
                                font: {
                                    color: (chart) => {
                                        if (chart.dataset){
                                            return chart.dataset.backgroundColor.map((color) => {
                                                const colorArray = color.match(/\d+/g).map(Number);
                                                const brightness = 0.2126 * colorArray[0] + 0.7152 * colorArray[1] + 0.0722 * colorArray[2];
                                                return brightness >= 128 ? 'rgb(102, 102, 102)' : 'rgb(255, 255, 255)';
                                            });
                                        } else {
                                            return 'rgb(255, 255, 255)';
                                        }
                                    },
                                    size: this.font_size,
                                    weight: 600,
                                    family: 'DM Sans',
                                }
                            },
                            formatter(value, context) {
                                // Only show labels if toggle is ON
                                if (!self.if_show_label) {
                                    return null;
                                }
                                
                                // Respect legend-hidden state (segment or dataset hidden)
                                const meta = context.chart.getDatasetMeta(context.datasetIndex);
                                if (meta) {
                                    const element = meta.data?.[context.dataIndex];
                                    if (element?.hidden) return null;
                                    if (meta.hidden) return null;
                                }
                                
                                // For pie charts, typically only show labels for the last dataset
                                if(context.datasetIndex < data.datasets.length-1) {
                                    return null;
                                }
                                
                                // If filter is applied, only show label for the filtered slice
                                if (self.if_chart_filter_applied && self.chart_filter_label.length > 0) {
                                    const chart_filter_index = self.chart_selected.labels.findIndex((label) => label == self.chart_filter_label[0]);
                                    if(context.dataIndex != chart_filter_index) {
                                        return null;
                                    }
                                }

                                // Format the value and percentage based on format_type
                                let label;
                                if(self.format_type == 'money') {
                                    let formattedMoney;
                                    if(self.show_compressed_numbers && value >= 1000) {
                                        formattedMoney = self.currency_abbr(value);
                                    } else if(self.max_value >= 10) {
                                        formattedMoney = self.currency_with_zero(value);
                                    } else {
                                        formattedMoney = self.currency_with_decimal(value);
                                    }
                                    label = formattedMoney + ' \n('+ self.percentage(value / self.total_value)  +')';
                                } else if(self.format_type == 'number') {
                                    let formattedNumber;
                                    if(self.show_compressed_numbers && value >= 1000) {
                                        formattedNumber = self.num_abbr(value);
                                    } else if(self.max_value >= 10) {
                                        formattedNumber = self.number_with_zero(value);
                                    } else {
                                        formattedNumber = self.number_with_decimal(value);
                                    }
                                    label = formattedNumber + ' \n('+ self.percentage(value / self.total_value)  +')';
                                } else if(self.format_type == 'percent') {
                                    label = self.percentage(value) + ' \n('+ self.percentage(value / self.total_value)  +')';
                                } else {
                                    label = value + ' \n('+ self.percentage(value / self.total_value)  +')';
                                }

                                return label;
                            },
                        }
                    },
                    title: {
                        display: false,
                    },
                },
            };
            return config;
        }
    }
})
</script>

